import { PERSONA_GENDERS, PersonaGender, UserPersona } from '../commonTypes';

export type PersonaImageFields = Pick<
	UserPersona,
	'name' | 'ageGroup' | 'biography' | 'goals' | 'needs' | 'painPoints' | 'motivations' | 'influences'
> & {
	gender?: string | null;
};

/** Clearly gendered first names used when persona.gender is missing (legacy personas). */
const FEMALE_FIRST_NAMES = new Set(
	[
		'mary',
		'emma',
		'lucy',
		'sophie',
		'hannah',
		'grace',
		'rachel',
		'nina',
		'maya',
		'sarah',
		'olivia',
		'ava',
		'amelia',
		'isabella',
		'charlotte',
		'eleanor',
		'victoria',
		'chloe',
		'laura',
		'anna',
		'emily',
		'elizabeth',
		'jennifer',
		'jessica',
		'amanda',
		'melissa',
		'rebecca',
		'stephanie',
		'lauren',
		'kate',
		'katie',
		'claire',
		'alice',
		'helen',
		'margaret',
		'susan',
		'patricia',
		'linda',
		'barbara',
		'maria',
		'jane',
		'lily',
		'ellie',
		'zoe',
		'leah',
		'nora',
		'hazel',
	].map((name) => name.toLowerCase())
);

const MALE_FIRST_NAMES = new Set(
	[
		'mathew',
		'matthew',
		'ben',
		'benjamin',
		'john',
		'daniel',
		'oliver',
		'theo',
		'theodore',
		'leo',
		'david',
		'james',
		'robert',
		'michael',
		'william',
		'thomas',
		'joseph',
		'andrew',
		'joshua',
		'mark',
		'paul',
		'kevin',
		'brian',
		'steven',
		'edward',
		'jason',
		'ryan',
		'jacob',
		'nathan',
		'henry',
		'peter',
		'jack',
		'noah',
		'ethan',
		'liam',
		'lucas',
		'mason',
		'owen',
		'luke',
		'gabriel',
		'anthony',
		'isaac',
		'julian',
		'aaron',
		'adam',
		'patrick',
		'george',
		'frank',
		'harold',
		'walter',
		'arthur',
		'charles',
		'christopher',
		'stephen',
		'samuel',
	].map((name) => name.toLowerCase())
);

const truncate = (value: string, maxLength: number): string => {
	const trimmed = value.replace(/\s+/g, ' ').trim();
	if (trimmed.length <= maxLength) return trimmed;
	return `${trimmed.slice(0, maxLength - 1).trimEnd()}…`;
};

const firstNameFrom = (name: string): string => {
	const match = name.trim().match(/^[\p{L}']+/u);
	return match?.[0]?.toLowerCase() || '';
};

export const normalizePersonaGender = (value?: string | null): PersonaGender | undefined => {
	const raw = (value || '').trim().toLowerCase();
	if (!raw) return undefined;
	if (PERSONA_GENDERS.includes(raw as PersonaGender)) return raw as PersonaGender;
	if (/^(f|female|woman|women|girl|she\/her|she)$/.test(raw)) return 'female';
	if (/^(m|male|man|men|boy|he\/him|he)$/.test(raw)) return 'male';
	if (/^(nb|non[-\s]?binary|enby|they\/them|they|genderqueer|gender.?fluid|agender)$/.test(raw)) {
		return 'non-binary';
	}
	return undefined;
};

export const inferPersonaGenderFromName = (name?: string | null): PersonaGender | undefined => {
	const firstName = firstNameFrom(name || '');
	if (!firstName) return undefined;
	if (FEMALE_FIRST_NAMES.has(firstName) && !MALE_FIRST_NAMES.has(firstName)) return 'female';
	if (MALE_FIRST_NAMES.has(firstName) && !FEMALE_FIRST_NAMES.has(firstName)) return 'male';
	return undefined;
};

export const resolvePersonaGender = (persona: PersonaImageFields): PersonaGender | undefined => {
	return normalizePersonaGender(persona.gender) || inferPersonaGenderFromName(persona.name);
};

const genderSubjectPhrase = (gender: PersonaGender | undefined, name: string): string => {
	const safeName = name.trim() || 'this person';
	if (gender === 'female') return `a woman named ${safeName}`;
	if (gender === 'male') return `a man named ${safeName}`;
	if (gender === 'non-binary') return `a non-binary person named ${safeName}`;
	return `a person named ${safeName}`;
};

/**
 * Build the text passed into persona / specialist image generation.
 * Name and explicit gender are required cues so downstream image models do not
 * default to the wrong presentation.
 */
export const buildPersonaImagePrompt = (persona: PersonaImageFields): string => {
	const gender = resolvePersonaGender(persona);
	const ageGroup = persona.ageGroup || '25–34';
	const parts = [
		`Portrait subject: ${genderSubjectPhrase(gender, persona.name)}`,
		`Age group: ${ageGroup}`,
		gender ? `Gender: ${gender} (must match exactly in the depiction)` : '',
		persona.biography?.trim() ? `Who they are: ${truncate(persona.biography, 220)}` : '',
		persona.needs?.trim() ? `Needs: ${truncate(persona.needs, 120)}` : '',
		persona.goals?.trim() ? `Goals: ${truncate(persona.goals, 120)}` : '',
		persona.painPoints?.trim() ? `Pain points: ${truncate(persona.painPoints, 100)}` : '',
		persona.motivations?.trim() ? `Motivations: ${truncate(persona.motivations, 100)}` : '',
		persona.influences?.trim() ? `Influences: ${truncate(persona.influences, 100)}` : '',
		'Output a single portrait of this specific person. Keep their name and gender consistent; do not swap gender.',
	];
	return parts.filter(Boolean).join('\n');
};
